package com.macrofocus.trenddisplay.timeseriesplot

import com.macrofocus.common.command.Future
import com.macrofocus.common.concurrent.Callable
import com.macrofocus.common.concurrent.ExecutorService
import com.macrofocus.common.crossplatform.CPHelper
import com.macrofocus.common.filter.Filter
import com.macrofocus.common.filter.FilterEvent
import com.macrofocus.common.filter.FilterListener
import com.macrofocus.common.interval.BoundedIntervalEvent
import com.macrofocus.common.interval.BoundedIntervalListener
import com.macrofocus.common.interval.MutableBoundedInterval
import com.macrofocus.common.interval.SimpleBoundedInterval
import com.macrofocus.common.geom.Dimension
import com.macrofocus.common.math.PI
import com.macrofocus.common.properties.*
import com.macrofocus.common.selection.*
import com.macrofocus.common.timer.CPTimer
import com.macrofocus.common.timer.CPTimerListener
import com.macrofocus.common.transform.*
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.plot.guide.Guide
import com.macrofocus.plot.guide.XNumberOneDScreenTransformAxisGuide
import com.macrofocus.plot.guide.YNumberOneDScreenTransformAxisGuide
import org.mkui.canvas.*
import org.mkui.color.MkColor
import org.mkui.color.colorOf
import org.mkui.component.CPFactory
import org.mkui.geom.*
import org.mkui.graphics.AbstractIDrawing
import org.mkui.graphics.IDrawing
import org.mkui.graphics.IGraphics
import org.mkui.graphics.colortheme.ColorTheme
import org.mkui.graphics.colortheme.LightColorTheme
import org.mkui.graphics.pressure.LinearPressure
import org.mkui.palette.FixedPalette
import org.mkui.palette.PaletteFactory
import org.mkui.rubberband.RubberBand
import org.mkui.rubberband.RubberbandDrawing
import org.mkui.rubberband.ScreenTransformRubberBand
import org.mkui.transform.MutableTwoDScreenTransform
import org.mkui.transform.SimpleTwoDScreenTransform
import org.mkui.transform.TwoDScreenTransform
import org.mkui.visual.VisualLayer
import org.mkui.visual.VisualListener
import org.molap.aggregates.cube.Group
import org.molap.dataframe.DataFrame
import org.molap.dataframe.DataFrameEvent
import org.molap.dataframe.DataFrameListener
import org.molap.series.TimeSeries
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sqrt

abstract class AbstractTimeSeriesPlotView<Row, Column>(
    protected var canvas: CPCanvas,
    private val factory: CPFactory,
    private val paletteFactory: PaletteFactory
) :
    TimeSeriesPlotView<Row, Column> {
    override var model: TimeSeriesPlotModel<Row, Column>? = null
        set(model) {
            if (field != null) {
                field!!.removeTimeSeriesPlotListener(listener)
                field!!.filter?.removeFilterListener(filterListener)
                if (field?.time != null) {
                    field!!.time.removeSingleSelectionListener(axisSelectionListener)
                }
                if (field?.y != null) field!!.y.removeSingleSelectionListener(axisSelectionListener)
            }

            field = model as TimeSeriesPlotModel<Row, Column>?

            createOverplots()

            olsRegression = null
            if (field != null) {
                field!!.addTimeSeriesPlotListener(listener)
                field!!.filter?.addFilterListener(filterListener)
                if (field?.time != null) {
                    val x: MutableSingleSelection<AxisModel<Row, Column>?> = field!!.time
                    x.addSingleSelectionListener(axisSelectionListener)
                    if (x.isActive && x.selected?.interval != null) {
                        x.selected!!.interval.addBoundedIntervalListener(intervalListener)
                    }
                }
                if (field?.y != null) {
                    val y: MutableSingleSelection<AxisModel<Row, Column>?> = field!!.y
                    y.addSingleSelectionListener(axisSelectionListener)
                    if (y.isActive && y.selected?.interval != null) {
                        y.selected!!.interval.addBoundedIntervalListener(intervalListener)
                    }
                }
            }

            updateIntervals()
            updateAxisType()
            resetXAxis()
            resetYAxis()

            timer.restart()
        }

    override val properties: MutableProperties<TimeSeriesPlotView.PropertyType> =
        EnumProperties<TimeSeriesPlotView.PropertyType>(
            TimeSeriesPlotView.PropertyType.values()
        )

    protected val timer: CPTimer

    override val xRangeModel: MutableBoundedInterval = SimpleBoundedInterval(0.0, 1.0, 0.0, 1.0)
    override val yRangeModel: MutableBoundedInterval = SimpleBoundedInterval(0.0, 1.0, 0.0, 1.0)
    override val screenTransform: MutableTwoDScreenTransform = SimpleTwoDScreenTransform(xRangeModel, yRangeModel)
    protected var rangesScreenTransformCoordinator: RangesScreenTransformCoordinator? = null

    override var xGuide: Guide = XNumberOneDScreenTransformAxisGuide(Guide.Type.Bottom, screenTransform.x)
    override var yGuide: Guide = YNumberOneDScreenTransformAxisGuide(Guide.Type.Left, screenTransform.y)

    override var isSelectionMode: Boolean = true
    override val rubberBand: RubberBand = ScreenTransformRubberBand(screenTransform)

    private val propertiesListener: PropertiesListener<TimeSeriesPlotView.PropertyType> =
        object : PropertiesListener<TimeSeriesPlotView.PropertyType> {
            override fun propertyChanged(name: TimeSeriesPlotView.PropertyType, event: PropertyEvent<Any?>) {
                createOverplots()
                scheduleUpdate()
            }
        }

    /** The parameters of the fitted regression line, computed when first drawn. */
    private var olsRegression: DoubleArray? = null

    private val filterListener: FilterListener<Row> = object : FilterListener<Row> {
        override fun filterChanged(event: FilterEvent<Row>) {
            olsRegression = null
        }
    }

    private val listener: TimeSeriesPlotListener = object : TimeSeriesPlotListener {
        override fun timeSeriesPlotChanged() {
            olsRegression = null
            resetXAxis()
            resetYAxis()

            scheduleUpdate()
        }
    }

    private val intervalListener: BoundedIntervalListener = object : BoundedIntervalListener {
        override fun boundedIntervalChanged(event: BoundedIntervalEvent) {
            updateIntervals()
        }
    }

    private val axisSelectionListener: SingleSelectionListener<AxisModel<Row, Column>?> =
        object : SingleSelectionListener<AxisModel<Row, Column>?> {
            override fun selectionChanged(event: SingleSelectionEvent<AxisModel<Row, Column>?>) {
                olsRegression = null
                if (event.previousSelection != null && event.previousSelection?.interval != null) {
                    event.previousSelection!!.interval.removeBoundedIntervalListener(intervalListener)
                }
                if (event.currentSelection != null && event.currentSelection?.interval != null) {
                    event.currentSelection!!.interval.addBoundedIntervalListener(intervalListener)
                }
                updateAxisType()
                updateIntervals()
            }
        }

    init {
        properties.createProperty<Boolean>(TimeSeriesPlotView.PropertyType.ShowFiltered, true)
        properties.createProperty<Boolean>(TimeSeriesPlotView.PropertyType.ShowGrid, true)
        properties.createProperty<Boolean>(TimeSeriesPlotView.PropertyType.Antialiasing, true)
        properties.createProperty<Any>(TimeSeriesPlotView.PropertyType.Rendering, Rendering.AlphaBlended)
        properties.createProperty(TimeSeriesPlotView.PropertyType.LineWidth, 1.0)
        properties.createProperty(TimeSeriesPlotView.PropertyType.GridThickness, 0.1f)
        properties.createProperty<MkColor>(TimeSeriesPlotView.PropertyType.ShowColor, colorOf(128, 128, 128))
        properties.createProperty<Boolean>(TimeSeriesPlotView.PropertyType.Regression, true)
        properties.createProperty(TimeSeriesPlotView.PropertyType.MarkerSize, 8.0)
        properties.createProperty<ColorTheme>(TimeSeriesPlotView.PropertyType.ColorTheme, LightColorTheme())
        properties.createProperty<Boolean>(TimeSeriesPlotView.PropertyType.DisplayLabels, true)
        properties.createProperty(TimeSeriesPlotView.PropertyType.Style, TimeSeriesPlotView.Style.Straight)
        properties.createProperty<Any>(TimeSeriesPlotView.PropertyType.Mode, TimeSeries.TimeMode.Absolute)
        properties.createProperty(TimeSeriesPlotView.PropertyType.Layout, TimeSeriesPlotView.Layout.Overlay)
        properties.addPropertiesListener(propertiesListener)

        timer = CPHelper.instance.createTimer("TimeSeriesPlotPlotResizer", 50, true, object : CPTimerListener {
            override fun timerTriggered() {
                if (width > 0 && height > 0) {
                    refresh()
                }
            }
        })

        screenTransform.x.screenMargins = MARGIN
        screenTransform.y.screenMargins = MARGIN

        mode.addPropertyListener(object : PropertyListener<TimeSeries.TimeMode> {
            override fun propertyChanged(event: PropertyEvent<TimeSeries.TimeMode>) {
                resetXAxis()
            }
        })

        layout.addPropertyListener(object : PropertyListener<TimeSeriesPlotView.Layout> {
            override fun propertyChanged(event: PropertyEvent<TimeSeriesPlotView.Layout>) {
                resetYAxis()
            }
        })

        canvas.addCanvasListener(object : CanvasListener {
            override fun sizeChange(width: Int, height: Int) {
                screenTransform.setScreenSize(width, height)
            }
        })

        screenTransform.addScreenTransformListener(object : ScreenTransformListener {
            override fun transformChanged(event: ScreenTransformEvent) {
                timer.restart()
            }
        })
    }

    protected abstract val width: Int

    protected abstract val height: Int

    protected abstract fun refresh()

    protected abstract fun repaint()

    protected abstract fun scheduleUpdate()

    override fun addMouseListener(l: MouseListener) {
        canvas.addMouseListener(l)
    }

    override fun addMouseMotionListener(l: MouseMotionListener) {
        canvas.addMouseMotionListener(l)
    }

    override fun addMouseWheelListener(l: MouseWheelListener) {
        canvas.addMouseWheelListener(l)
    }

    override fun addKeyListener(listener: KeyListener) {
        canvas.addKeyListener(listener)
    }

    fun createOverplots() {
        if (model != null) {
            canvas.removeAllLayers()

            populateOverplots()
        }
    }

    protected fun populateOverplots() {
//        canvas.addLayer(object : AbstractMasterVisualLayerIDrawing(model!!.masterVisual!!.probed, model!!.masterDataFrame!!) {
//            val configuration: TimeSeriesPlotConfiguration = getTimeSeriesPlotConfiguration(style.value, colorTheme.value.probing)
//            init {
//                configuration.setLineWidth(lineWidth.value.toFloat() * 2f)
//            }
//
//            override fun draw(g: IGraphics, group: Group, width: Double, height: Double) {
//                val timeSeries: TimeSeries<Int,*>? =
//                    model!!.masterDataFrame!!.getValueAt(group!!, model!!.y.selected!!.column.toString()) as TimeSeries<Int,*>?
//                if (timeSeries != null) {
//                    configuration.getRenderer().draw(timeSeries, screenTransform, g, mode.value, configuration, true, false)
//                }
//            }
//        })

        val timeSeriesFilteredDrawing: IDrawing = object : AbstractMasterVisualLayerIDrawing(model!!.masterVisual!!.filtered, model!!.masterDataFrame!!) {
            var configuration: TimeSeriesPlotConfiguration =
                getTimeSeriesPlotConfiguration(style.value!!, colorTheme.value.ghostedPalette.colors.first())
            init {
                configuration.setLineWidth(lineWidth.value.toFloat()  / 2f)
            }

            override val isActive: Boolean
                get() = showFiltered.value!! && super.isActive

            override fun draw(g: IGraphics, group: Group, width: Double, height: Double) {
                val timeSeries: TimeSeries<Int,*>? =
                    model!!.masterDataFrame!!.getValueAt(group!!, model!!.y.selected!!.column.toString()) as TimeSeries<Int,*>?
                if (timeSeries != null) {
                    configuration.getRenderer().draw(timeSeries, screenTransform, g, mode.value, configuration, false, false)
                }
            }
        }

        val filteredDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.filtered) {
            override val isActive: Boolean
                get() = showFiltered.value!! && super.isActive

            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    if (model!!.sizeAxisModel != null) {
                        val size = getMarkerSize(AxisModel.DATA, row)
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    } else {
                        val size = max(1.0, (getMarkerSize(AxisModel.DATA, row) / 2).toDouble()).toInt()
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    }
                }
            }
        }

        val visibleDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.visible) {
            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    val size = getMarkerSize(AxisModel.DATA, row)
                    val radius = size / 2
                    g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                }
            }
        }

        val colorMappedDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.colorMapped) {
            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    g.setColor(model!!.colorMapping!!.getColor(row)!!)
                    val size = getMarkerSize(AxisModel.DATA, row)
                    val radius = size / 2.0
                    g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                }
            }
        }

        val coloredDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.colored) {
            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    g.setColor(model!!.coloring!!.getColor(row)!!)
                    val size = getMarkerSize(AxisModel.DATA, row)
                    val radius = size / 2.0
                    g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                }
            }
        }

        val singleSelectedDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.singleSelected) {
            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    g.setColor(colorTheme.value.selection)
                    if (model?.sizeAxisModel != null) {
                        // ToDo: Fix bigger so that they occlude the density version which is not pixel perfect
                        val size = getMarkerSize(AxisModel.DATA, row) + 2
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    } else {
                        val size = getMarkerSize(AxisModel.DATA, row) + 2
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    }
                }
            }
        }

        val multiSelectedDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.multipleSelected) {
            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    g.setColor(colorTheme.value.selection)
                    if (model?.sizeAxisModel != null) {
                        // ToDo: Fix bigger so that they occlude the density version which is not pixel perfect
                        val size = getMarkerSize(AxisModel.DATA, row) + 2
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    } else {
                        val size = getMarkerSize(AxisModel.DATA, row) + 2
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    }
                }
            }
        }

        val timeSeriesProbedDrawing: IDrawing = object : AbstractMasterVisualLayerIDrawing(model!!.masterVisual!!.probed, model!!.masterDataFrame!!) {
            var configuration: TimeSeriesPlotConfiguration =
                getTimeSeriesPlotConfiguration(style.value, colorTheme.value.probing)
            init {
                configuration.setLineWidth(lineWidth.value.toFloat() * 1.5f)
            }

            override fun draw(g: IGraphics, group: Group, width: Double, height: Double) {
                val timeSeries: TimeSeries<Int,*>? =
                    model!!.masterDataFrame!!.getValueAt(group, model!!.y.selected!!.column.toString()) as TimeSeries<Int,*>?
                if (timeSeries != null) {
                    configuration.getRenderer().draw(timeSeries, screenTransform, g, mode.value, configuration, true, false)
                }
            }
        }

        val probedDrawing: IDrawing = object : AbstractVisualLayerIDrawing(model!!.visual!!.probed) {
            override fun draw(g: IGraphics, row: Row, width: Double, height: Double) {
                val mp: Point? = getPoint(AxisModel.DATA, row)

                if (mp != null) {
                    if (model!!.sizeAxisModel != null) {
                        // ToDo: Fix bigger so that they occlude the density version which is not pixel perfect
                        run {
                            g.setColor(colorTheme.value.probing)
                            val size = getMarkerSize(AxisModel.DATA, row) + 2
                            val radius = size / 2.0
                            g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                        }

                        if (model!!.selection!!.isSelected(row)) {
                            g.setColor(colorTheme.value.selection)
                            val size = getMarkerSize(AxisModel.DATA, row)
                            val radius = size / 2.0
                            g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                        }
                    } else {
                        run {
                            g.setColor(colorTheme.value.probing)
                            val size = getMarkerSize(AxisModel.DATA, row) + 2
                            val radius = size / 2.0
                            g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                        }

                        if (model!!.selection!!.isSelected(row)) {
                            g.setColor(colorTheme.value.selection)
                            val size = getMarkerSize(AxisModel.DATA, row)
                            val radius = size / 2.0
                            g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                        }
                    }
                }
            }
        }

        canvas.addLayer(object : AbstractIDrawing() {
            override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
                val background: MkColor? = colorTheme.value.background
                if (background != null) {
                    g.setColor(background)
                    g.fillRectangle2D(Rectangle2D.Double(0.0, 0.0, width, height))
                }
            }
        })

        // The grid of the guides, as SwingTimeSeriesPlotView
        canvas.addLayer(object : AbstractIDrawing() {
            override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
                val thickness = gridThickness.value
                val color = showColor.value as? MkColor
                if (showGrid.value && thickness > 0 && color != null) {
                    g.setColor(color)
                    g.setLineWidth(thickness.toDouble())
                    val size = Dimension(width.toInt(), height.toInt())
                    xGuide.drawGrid(g, size)
                    yGuide.drawGrid(g, size)
                    g.setLineWidth(1.0)
                }
            }
        })

        if (showFiltered.value) {
            canvas.addDensityLayer(rendering!!.value!!, timeSeriesFilteredDrawing,  LinearPressure(), object : PaletteProvider {
                override val palette: FixedPalette
                    get() = colorTheme.value.ghostedPalette
            });
        }

        //
        val timeSeriesVisibleDrawing: AbstractMasterVisualLayerIDrawing =
            object : AbstractMasterVisualLayerIDrawing(model!!.masterVisual!!.visible, model!!.masterDataFrame!!) {
                var configuration: TimeSeriesPlotConfiguration =
                    getTimeSeriesPlotConfiguration(style.value, colorOf(112, 120, 135))
                init {
                    configuration.setLineWidth(lineWidth.value.toFloat())
                }

                override fun draw(g: IGraphics, group: Group, width: Double, height: Double) {
                    if (false) {
                        val dataFrame: DataFrame<*,*,*>? = model?.masterDataFrame?.getValueAt(group, "DataFrame") as DataFrame<*,*,*>?
                        if (dataFrame != null) {
                            configuration.getRenderer().draw(
                                DataFrameTimeSeries<Int, Column, Any?>(
                                    dataFrame as DataFrame<Int, Column, Any?>,
                                    model!!.time.selected!!.column,
                                    model!!.y.selected!!.column
                                ), screenTransform, g, mode.value, configuration, false, false
                            )
                        }
                    } else {
                        val timeSeries: TimeSeries<*,*>? =
                            model?.masterDataFrame?.getValueAt(group, model?.y?.selected?.column.toString()) as TimeSeries<*,*>?
                        if (timeSeries != null) {
                            val y1: Int
                            val y2: Int

                            val worldY1: Double
                            val worldY2: Double

                            //                        int translateX;
//                        int translateY;
                            val st: TwoDScreenTransform

                            when (layout.value) {
                                TimeSeriesPlotView.Layout.Overlay -> {
                                    y1 = 0
                                    y2 = screenTransform.screenHeight
                                    //                                translateY = 0;
                                    worldY1 = screenTransform.y.worldInterval.start
                                    worldY2 = screenTransform.y.worldInterval.end
                                }

                                TimeSeriesPlotView.Layout.Stack -> {
                                    //                                y1 = 0;
//                                y2 = screenTransform.getScreenHeight() / model.getMasterDataFrame().getRowCount();
//                                translateY = screenTransform.getScreenHeight() *  model.getMasterDataFrame().getRowAddress(group) / model.getMasterDataFrame().getRowCount();
//                                worldY1 = screenTransform.getY().getWorldInterval().getStart();
//                                worldY2 = screenTransform.getY().getWorldInterval().getEnd();
                                    val i: Int = model!!.masterDataFrame!!.getRowAddress(group)
                                    val count: Int = model!!.masterDataFrame!!.rowCount
                                    y1 = screenTransform.yWorldToScreen((count - i).toDouble())
                                    y2 = screenTransform.yWorldToScreen((count - i - 1).toDouble())
                                    //                                translateY = y1;
                                    worldY1 = model!!.yAxisModel!!.maximum
                                    worldY2 = model!!.yAxisModel!!.minimum
                                }

                                else -> {
                                    val i: Int = model!!.masterDataFrame!!.getRowAddress(group)
                                    val count: Int = model!!.masterDataFrame!!.rowCount
                                    y1 = screenTransform.yWorldToScreen((count - i).toDouble())
                                    y2 = screenTransform.yWorldToScreen((count - i - 1).toDouble())
                                    worldY1 = model!!.yAxisModel!!.maximum
                                    worldY2 = model!!.yAxisModel!!.minimum
                                }
                            }


                            val screenWidth: Int = screenTransform.screenWidth
                            val screenHeight = y2 - y1
                            val yTransform: MutableOneDScreenTransform = ComplexOneDScreenTransform(
                                SimpleBoundedInterval(worldY1, worldY2 - worldY1),
                                y1,
                                y2,
                                true,
                                false
                            )
                            yTransform.screenMargins = MARGIN
                            st = SimpleTwoDScreenTransform(
                                screenTransform.x,
                                yTransform,
                                screenWidth,
                                screenHeight,
                                1.0
                            )

                            // g.translate(translateX, translateY);
                            configuration.getRenderer().draw(timeSeries as TimeSeries<Int,*>, st, g, mode.value, configuration, false, false)

                            //  g.translate(-translateX, -translateY);
                        }
                    }
                }
            }
        canvas.addDensityLayer(rendering.value, timeSeriesVisibleDrawing, LinearPressure(), object : PaletteProvider {
            override val palette: FixedPalette
                get() = colorTheme.value.visiblePalette
        })

        //        canvas.addLayer(timeSeriesVisibleDrawing);
        val timeSeriesSelectedDrawing =
            object : AbstractMasterVisualLayerIDrawing(model!!.masterVisual!!.selected, model!!.masterDataFrame!!) {
                var configuration: TimeSeriesPlotConfiguration =
                    getTimeSeriesPlotConfiguration(style.value, colorTheme.value.selection)

                init {
                    configuration.setLineWidth(lineWidth.value.toFloat() * 2f)
                }

                override fun draw(g: IGraphics, group: Group, width: Double, height: Double) {
                    val timeSeries: TimeSeries<Int, *>? =
                        model!!.masterDataFrame!!.getValueAt(
                            group!!,
                            model!!.y.selected!!.column.toString()
                        ) as TimeSeries<Int, *>?
                    if (timeSeries != null) {
                        g.setLineWidth(lineWidth.value)
                        configuration.getRenderer()
                            .draw(timeSeries, screenTransform, g, mode.value, configuration, false, true)
                    }
                }
            }
        canvas.addLayer(timeSeriesSelectedDrawing)

        canvas.addLayer(timeSeriesProbedDrawing)
//        canvas.addLayer(object : AbstractMasterVisualLayerIDrawing(model!!.masterVisual!!.probed, model!!.masterDataFrame!!) {
//            var configuration: TimeSeriesPlotConfiguration =
//                getTimeSeriesPlotConfiguration(style.value!!, colorTheme.value.probing)
//            init {
//                configuration.setLineWidth(lineWidth.value.toFloat() * 2f)
//            }
//
//            override fun draw(g: IGraphics, group: Group, width: Double, height: Double) {
//                val timeSeries: TimeSeries<Int,*>? =
//                    model!!.masterDataFrame!!.getValueAt(group!!, model!!.y.selected!!.column.toString()) as TimeSeries<Int,*>?
//                if (timeSeries != null) {
//                    g.setLineWidth(lineWidth.value)
//                    configuration.getRenderer().draw(timeSeries, screenTransform, g, mode.value, configuration, true, false)
//                }
//            }
//        })


//        canvas.addDensityLayer(getRendering().getValue(), visibleDrawing, new LinearPressure(), new CPCanvas.PaletteProvider() {
//            @Override
//            public FixedPalette getPalette() {
//                return getColorTheme().getValue().getVisiblePalette();
//            }
//        });
//
//        canvas.addAveragingLayer(getRendering().getValue(), colorMappedDrawing);
//
//        canvas.addBufferedLayer(coloredDrawing);
//
//        canvas.addDensityLayer(getRendering().getValue(), multiSelectedDrawing, new LinearPressure(), new CPCanvas.PaletteProvider() {
//            @Override
//            public FixedPalette getPalette() {
//                return getColorTheme().getValue().getSelectedPalette();
//            }
//        });
//
//        canvas.addLayer(singleSelectedDrawing);

//        canvas.addLayer(timeSeriesProbedDrawing);
//
//        canvas.addLayer(new AnnotationIDrawing(model.getVisual().getAnnotationProbing(), factory.createRGBAColor(255, 150, 0, 200)));
//        canvas.addLayer(new AnnotationIDrawing(model.getVisual().getAnnotationSelection(), factory.createRGBAColor(255, 100, 0, 200)));
//
        // The fitted regression line of the values of the visible rows, as SwingTimeSeriesPlotView
        canvas.addLayer(object : AbstractIDrawing() {
            override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
                val model = model ?: return
                val time = model.timeAxisModel ?: return
                val y = model.yAxisModel ?: return
                if (!regression.value || (model.visual?.active?.objectCount ?: 0) <= 2) return
                val regression = olsRegression ?: computeOLSRegression(model, time, y).also { olsRegression = it }
                if (regression[1].isNaN() || regression[0].isNaN()) return
                val minX = time.minimum
                val maxX = time.maximum
                val s1 = screenTransform.worldToScreen(Point2D.Double(minX, regression[0] + regression[1] * minX))
                val s2 = screenTransform.worldToScreen(Point2D.Double(maxX, regression[0] + regression[1] * maxX))
                g.setColor(colorOf(0, 0, 255, 100))
                g.setLineWidth(2.0)
                g.drawLine(s1.x.toDouble(), s1.y.toDouble(), s2.x.toDouble(), s2.y.toDouble())
                g.setLineWidth(1.0)
            }
        })

        canvas.addLayer(object : RubberbandDrawing(rubberBand) {
            override val colorTheme: Property<ColorTheme>
                get() = this@AbstractTimeSeriesPlotView.colorTheme
        })
    }

    /**
     * Returns the parameters 'a' and 'b' for an equation y = a + bx, fitted to the values of the visible rows using
     * ordinary least squares regression, as [a, b].
     */
    private fun computeOLSRegression(model: TimeSeriesPlotModel<Row, Column>, time: AxisModel<Row, Column>, y: AxisModel<Row, Column>): DoubleArray {
        val n = model.visual!!.active.objectCount
        var sumX = 0.0
        var sumY = 0.0
        var sumXX = 0.0
        var sumXY = 0.0
        for (row in model.visual!!.visible) {
            val v1 = time.getValue(AxisModel.DATA, row)
            val v2 = y.getValue(AxisModel.DATA, row)
            if (v1 != null && v2 != null) {
                val x = v1.toDouble()
                val v = v2.toDouble()
                sumX += x
                sumY += v
                sumXX += x * x
                sumXY += x * v
            }
        }
        val sxx = sumXX - sumX * sumX / n
        val sxy = sumXY - sumX * sumY / n
        val slope = sxy / sxx
        return doubleArrayOf(sumY / n - slope * (sumX / n), slope)
    }

    private fun getTimeSeriesPlotConfiguration(
        style: TimeSeriesPlotView.Style,
        color: MkColor
    ): TimeSeriesPlotConfiguration {
        val configuration: TimeSeriesPlotConfiguration
        when (style) {
            TimeSeriesPlotView.Style.Step -> {
                val renderer: TimeSeriesPlotRenderer = GeneralPathTimeSeriesRenderer<Any>()
                configuration = TimeSeriesPlotConfiguration(
                    renderer,
                    color,
                    TimeSeriesPlotConfiguration.LineType.Step,
                    TimeSeriesPlotConfiguration.MarkerType.Point,
                    false
                )
            }

            TimeSeriesPlotView.Style.Straight -> {
                val renderer: TimeSeriesPlotRenderer = GeneralPathTimeSeriesRenderer<Any>()
                configuration = TimeSeriesPlotConfiguration(
                    renderer,
                    color,
                    TimeSeriesPlotConfiguration.LineType.Straight,
                    TimeSeriesPlotConfiguration.MarkerType.Point,
                    false
                )
            }

            TimeSeriesPlotView.Style.Curved -> {
                val renderer: TimeSeriesPlotRenderer = CardinalSplineTimeSeriesRenderer()
                configuration = TimeSeriesPlotConfiguration(
                    renderer,
                    color,
                    TimeSeriesPlotConfiguration.LineType.Straight,
                    TimeSeriesPlotConfiguration.MarkerType.Point,
                    false
                )
            }

            else -> {
                val renderer: TimeSeriesPlotRenderer = HeatmapTimeSeriesRenderer(paletteFactory)
                configuration = TimeSeriesPlotConfiguration(
                    renderer,
                    color,
                    TimeSeriesPlotConfiguration.LineType.Straight,
                    TimeSeriesPlotConfiguration.MarkerType.Point,
                    false
                )
            }
        }
        return configuration
    }

    fun resetXAxis() {
        if (model?.timeAxisModel != null) {
            when (mode.value) {
                TimeSeries.TimeMode.Absolute -> {
                    val min: Double = model!!.timeAxisModel!!.minimum
                    val max: Double = model!!.timeAxisModel!!.maximum

                    xRangeModel.setMinMax(min, max)
                    xRangeModel.setValue(min, max - min)
                }

                TimeSeries.TimeMode.Relative -> {
                    val min = 0.0
                    val max: Double = model!!.getMaxDuration(model!!.y.selected!!.column)

                    xRangeModel.setMinMax(min, max)
                    xRangeModel.setValue(min, max - min)
                }

                TimeSeries.TimeMode.Sequential -> {
                    val min = 0.0
                    val max: Double = model!!.getMaxEvents(model!!.y.selected!!.column).toDouble()

                    xRangeModel.setMinMax(min, max)
                    xRangeModel.setValue(min, max - min)
                }

            }
        }
    }

    fun resetYAxis() {
        if (model?.yAxisModel != null) {
            when (layout.value) {
                TimeSeriesPlotView.Layout.Overlay -> {
                    val min: Double = model!!.yAxisModel!!.minimum
                    val max: Double = model!!.yAxisModel!!.maximum

                    yRangeModel.setMinMax(min, max)
                    yRangeModel.setValue(min, max - min)
                }

                TimeSeriesPlotView.Layout.Stack -> {
                    val min = 0.0
                    val max: Double = model!!.masterDataFrame!!.rowCount.toDouble()

                    yRangeModel.setMinMax(min, max)
                    yRangeModel.setValue(min, max - min)
                }
            }
        }
    }

    /**
     * {@inheritDoc}
     */
    override fun zoom(animate: Boolean, x1: Double, x2: Double, y1: Double, y2: Double) {
        xRangeModel.setValue(x1, x2 - x1)
        yRangeModel.setValue(y1, y2 - y1)
    }

    protected fun updateIntervals() {
        val x: MutableSingleSelection<AxisModel<Row, Column>?>? = model?.time
        val y: MutableSingleSelection<AxisModel<Row, Column>?>? = model?.y

        //        if (x.isActive() && x.getSelected().getInterval() != null) {
//            final double min = x.getSelected().getInterval().getMinimum();
//            final double max = x.getSelected().getInterval().getMaximum();
//            xRangeModel.setMinMax(min, max);
//            xRangeModel.setValue(min, max - min);
//        } else {
        resetXAxis()
        //        }
//        if (y.isActive() && y.getSelected().getInterval() != null) {
//            final double min = y.getSelected().getInterval().getMinimum();
//            final double max = y.getSelected().getInterval().getMaximum();
//            yRangeModel.setMinMax(min, max);
//            yRangeModel.setValue(min, max - min);
//        } else {
        resetYAxis()

        //        }

//        screenTransform.setIntervals(x.getSelected().getInterval(), y.getSelected().getInterval());
        if (rangesScreenTransformCoordinator != null) {
            rangesScreenTransformCoordinator!!.setRanges(xRangeModel, yRangeModel)
        }

        scheduleUpdate()
    }

    protected abstract fun updateAxisType()

    override fun getClosestGroup(x: Int, y: Int): Group? {
        var bestdistance = Double.MAX_VALUE
        var bestrow: Group? = null
        if (model != null) {
            for (row in 0 until model!!.masterDataFrame!!.rowCount) {
                val rowObject: Group = model!!.masterDataFrame!!.getRowKey(row)
                val filter: Filter<Group> = model!!.masterVisual!!.filter
                if (!filter.isFiltered(rowObject)) {
                    var configuration: TimeSeriesPlotConfiguration =
                        getTimeSeriesPlotConfiguration(style.value, colorTheme.value.selection)
                    val timeSeries: TimeSeries<Int, *>? =
                        model!!.masterDataFrame!!.getValueAt(
                            rowObject!!,
                            model!!.y.selected!!.column.toString()
                        ) as TimeSeries<Int, *>?
                    val (marker, line) = configuration.getRenderer().getShape(timeSeries, screenTransform, mode.value, configuration)
                    if (line != null) {
                        val pi: PathIterator = line.getPathIterator()
                        val data = FloatArray(6)
                        var previousX = 0
                        var previousY = 0
                        var lastdistance = Double.MAX_VALUE
                        val willNotImprove = false
                        while (!willNotImprove && !pi.isDone) {
                            val segType: Int = pi.currentSegment(data)
                            when (segType) {
                                PathIterator.SEG_MOVETO -> {
                                    previousX = data[0].toInt()
                                    previousY = data[1].toInt()
                                    val distance =
                                        distance(previousX.toDouble(), previousY.toDouble(), x.toDouble(), y.toDouble())
                                    if (distance < bestdistance) {
                                        bestdistance = distance
                                        bestrow = rowObject
                                    }
                                    // ToDo: Optimization to be verified
                                    if (distance > lastdistance) {
//                                        willNotImprove = true;
                                    }
                                    lastdistance = distance
                                }

                                PathIterator.SEG_LINETO -> {
                                    val currentX = data[0].toInt()
                                    val currentY = data[1].toInt()
                                    val distance: Double = Line2D.ptSegDist(
                                        previousX.toDouble(),
                                        previousY.toDouble(),
                                        currentX.toDouble(),
                                        currentY.toDouble(),
                                        x.toDouble(),
                                        y.toDouble()
                                    )
                                    if (distance < bestdistance) {
                                        bestdistance = distance
                                        bestrow = rowObject
                                    }

                                    // ToDo: Optimization to be verified
                                    if (distance > lastdistance) {
//                                        willNotImprove = true;
                                    }
                                    lastdistance = distance
                                    previousX = currentX
                                    previousY = currentY
                                }

                                else -> throw Error("Segmented type unsupported")
                            }
                            pi.next()
                        }
                    }
                }
            }
        }
        return bestrow
    }

    override fun getClosestRow(x: Int, y: Int): Row? {
        var bestdistance = Double.MAX_VALUE
        var bestrow: Row? = null
        val visible: VisualLayer<Row> = model!!.visual!!.active
        for (row in visible) {
            val p: Point2D? = getPoint(AxisModel.DATA, row)
            if (p != null) {
                val distance: Double = Point2D.distance(p.x, p.y, x.toDouble(), y.toDouble())
                if (distance < bestdistance) {
                    bestdistance = distance
                    bestrow = row
                }
            }
        }
        return bestrow
    }

    override fun getRows(rect: Rectangle2D): List<Row> {
        val visible: VisualLayer<Row> = model!!.visual!!.active
        val list: MutableList<Row> = ArrayList<Row>()
        for (row in visible) {
            val p: Point2D? = getPoint(AxisModel.DATA, row)
            if (p != null) {
                if (rect.contains(p)) {
                    list.add(row)
                }
            }
        }
        return list
    }

    override var showFiltered: MutableProperty<Boolean>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.ShowFiltered) as MutableProperty<Boolean>
        set(showFiltered) {
            properties.replaceProperty(TimeSeriesPlotView.PropertyType.ShowFiltered, showFiltered)
        }

    override val showGrid: MutableProperty<Boolean>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.ShowGrid) as MutableProperty<Boolean>

    override var antialiasing: MutableProperty<Boolean>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.Antialiasing) as MutableProperty<Boolean>
        set(antialiasing) {
            properties.replaceProperty(TimeSeriesPlotView.PropertyType.Antialiasing, antialiasing)
        }

    override var rendering: MutableProperty<Rendering>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.Rendering) as MutableProperty<Rendering>
        set(rendering) {
            properties.replaceProperty(TimeSeriesPlotView.PropertyType.Rendering, rendering)
        }

    override val lineWidth: MutableProperty<Double>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.LineWidth) as MutableProperty<Double>

    override val gridThickness: MutableProperty<Float>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.GridThickness) as MutableProperty<Float>

    override val showColor: MutableProperty<Any?>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.ShowColor)

    override val regression: MutableProperty<Boolean>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.Regression) as MutableProperty<Boolean>

    override val markerSize: MutableProperty<Double>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.MarkerSize) as MutableProperty<Double>

    override var colorTheme: MutableProperty<ColorTheme>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.ColorTheme) as MutableProperty<ColorTheme>
        set(colorTheme) {
            properties.replaceProperty(TimeSeriesPlotView.PropertyType.ColorTheme, colorTheme)
        }

    override val displayLabels: MutableProperty<Boolean>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.DisplayLabels) as MutableProperty<Boolean>

    override val mode: MutableProperty<TimeSeries.TimeMode>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.Mode) as MutableProperty<TimeSeries.TimeMode>

    override val layout: MutableProperty<TimeSeriesPlotView.Layout>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.Layout) as MutableProperty<TimeSeriesPlotView.Layout>

    override val style: MutableProperty<TimeSeriesPlotView.Style>
        get() = properties.getProperty(TimeSeriesPlotView.PropertyType.Style) as MutableProperty<TimeSeriesPlotView.Style>

    abstract inner class AbstractMasterVisualLayerIDrawing protected constructor(
        visualLayer: VisualLayer<Group>,
        dataFrame: DataFrame<Group, String, Any?>
    ) :
        AbstractIDrawing() {
        private val visualLayer: VisualLayer<Group> = visualLayer

        init {
            visualLayer.addVisualListener(object : VisualListener {
                override fun visualChanged() {
                    notifyIDrawingChanged()
                }
            })

            dataFrame.addDataFrameListener(object : DataFrameListener<Group, String> {
                override fun dataFrameChanged(event: DataFrameEvent<Group, String>) {
                    println(event)
                    notifyIDrawingChanged()
                }
            })
        }

        override val isActive: Boolean
            get() = visualLayer.isActive

        override fun draw(g: IGraphics, point: Point2D?, gw: Double, gh: Double, clipBounds: Rectangle) {
            if (visualLayer.objectCount > 0) {
                if (!MULTITHREADED || !g.isThreadSafe()) {
                    for (row in visualLayer) {
                        draw(g, row, gw, gh)
                    }
                } else {
                    val nTasks: Int = min(
                        executor!!.getAvailableProcessors(),
                        visualLayer.objectCount
                    ) // Determine number of tasks i.e. threads
                    val nRowsPerTask: Int = visualLayer.objectCount / nTasks

                    val todo: MutableList<Callable<Any?>> =
                        ArrayList<Callable<Any?>>(nTasks)

                    for (nTask in 0..<nTasks)  // Iterate over all tasks
                    {
                        val fromRow = nTask * nRowsPerTask // the tasks first row
                        val toRow =
                            if (nTask < (nTasks - 1)) fromRow + nRowsPerTask else visualLayer.objectCount // the tasks last row

                        todo.add(object : Callable<Any?> {
                            // Create and define the task
                            private val from = fromRow
                            private val to = toRow

//                            @Throws(Exception::class)
                            override fun call(): Any? {
                                for (row in visualLayer.iterable(from, to - 1)) {
                                    draw(g, row, gw, gh)
                                }
                                return null
                            }
                        })
                    }

                    try {
                        val answers: List<Future<Any?>> = executor!!.invokeAll(todo)
                        for (answer in answers) {
//                            try {
                                answer.get()
//                            } catch (e: InterruptedException) {
//                                e.printStackTrace()
//                            } catch (e: ExecutionException) {
//                                e.cause.printStackTrace()
//                            }
                        }
//                    } catch (e: InterruptedException) {
                        // can happen
                    } catch (e: Exception) {
                        e.printStackTrace() // something wrong, maybe
                    }
                }
            }
        }

        abstract fun draw(g: IGraphics, row: Group, width: Double, height: Double)
    }

    abstract inner class AbstractVisualLayerIDrawing protected constructor(visualLayer: VisualLayer<Row>) :
        AbstractIDrawing() {
        private val visualLayer: VisualLayer<Row> = visualLayer

        init {
            visualLayer.addVisualListener(object : VisualListener {
                override fun visualChanged() {
                    notifyIDrawingChanged()
                }
            })
        }

        override val isActive: Boolean
            get() = visualLayer.isActive

        override fun draw(g: IGraphics, point: Point2D?, gw: Double, gh: Double, clipBounds: Rectangle) {
            if (visualLayer.objectCount > 0) {
                if (!MULTITHREADED || !g.isThreadSafe()) {
                    for (row in visualLayer) {
                        draw(g, row, gw, gh)
                    }
                } else {
                    val nTasks: Int = min(
                        executor!!.getAvailableProcessors(),
                        visualLayer.objectCount
                    ) // Determine number of tasks i.e. threads
                    val nRowsPerTask: Int = visualLayer.objectCount / nTasks

                    val todo: MutableList<Callable<Any?>> =
                        ArrayList<Callable<Any?>>(nTasks)

                    for (nTask in 0..<nTasks)  // Iterate over all tasks
                    {
                        val fromRow = nTask * nRowsPerTask // the tasks first row
                        val toRow =
                            if (nTask < (nTasks - 1)) fromRow + nRowsPerTask else visualLayer.objectCount // the tasks last row

                        todo.add(object : Callable<Any?> {
                            // Create and define the task
                            private val from = fromRow
                            private val to = toRow

                            override fun call(): Any? {
                                for (row in visualLayer.iterable(from, to - 1)) {
                                    draw(g, row, gw, gh)
                                }
                                return null
                            }
                        })
                    }

                    try {
                        val answers: List<Future<Any?>> = executor!!.invokeAll(todo)
                        for (answer in answers) {
//                            try {
                                answer.get()
//                            } catch (e: InterruptedException) {
//                                e.printStackTrace()
//                            } catch (e: ExecutionException) {
//                                e.cause.printStackTrace()
//                            }
                        }
//                    } catch (e: InterruptedException) {
                        // can happen
                    } catch (e: Exception) {
                        e.printStackTrace() // something wrong, maybe
                    }
                }
            }
        }

        abstract fun draw(g: IGraphics, row: Row, width: Double, height: Double)
    }

    fun getTimeSeries(layer: Int, group: Group, mode: TimeSeries.TimeMode, lineType: TimeSeriesPlotConfiguration.LineType): GeneralPath? {
        if (model?.timeAxisModel != null && model?.yAxisModel != null) {
            val timeSeries: TimeSeries<Int,*>? =
                model!!.masterDataFrame!!.getValueAt(group!!, model!!.y.selected!!.column.toString()) as TimeSeries<Int,*>?

            if(timeSeries != null) {
                val linePath: GeneralPath = GeneralPath()
                var previous: RangePoint? = null

                for (i in timeSeries.keys()!!) {
                    val x: Int = screenTransform.xWorldToScreen(timeSeries.getTime(mode, i).toDouble())
                    val yValue: Any? = timeSeries.get(i)
                    if (yValue != null) {
                        val y: Int = screenTransform.yWorldToScreen((yValue as Number).toDouble())

                        if (previous != null) {
                            if (x != previous.x) {
                                if (previous.yMin != previous.yMax) {
                                    if (lineType != TimeSeriesPlotConfiguration.LineType.None) {
                                        linePath.lineTo(previous.x.toDouble(), previous.yMin.toDouble())
                                        linePath.lineTo(previous.x.toDouble(), previous.yMax.toDouble())
                                        linePath.lineTo(previous.x.toDouble(), previous.y.toDouble())
                                    }
                                }
                                if (lineType != TimeSeriesPlotConfiguration.LineType.None) {
                                    if (lineType == TimeSeriesPlotConfiguration.LineType.Step) {
                                        linePath.lineTo(x.toDouble(), previous.y.toDouble())
                                    }
                                    linePath.lineTo(x.toDouble(), y.toDouble())
                                }
                            }
                            previous.setValue(x, y)
                        } else {
                            previous = RangePoint()
                            previous!!.setValue(x, y)
                            linePath.moveTo(x.toDouble(), y.toDouble())
                            linePath.lineTo(x.toDouble(), y.toDouble())
                        }
                    } else {
                        previous = null
                    }
                }
                return linePath
            } else {
                return null
            }
        } else {
            return null
        }
    }


    @Deprecated("Use getTimeSeries instead")
    fun getPoint(layer: Int, row: Row): Point? {
        if (model?.timeAxisModel != null && model?.yAxisModel != null) {
            val v1: Number? = model?.timeAxisModel?.getValue(layer, row)
            val v2: Number? = model?.yAxisModel?.getValue(layer, row)
            return if (v1 != null && v2 != null) {
                Point(
                    screenTransform.x.worldToScreen(v1.toDouble()),
                    screenTransform.y.worldToScreen(v2.toDouble())
                )
            } else {
                null
            }
        } else {
            return null
        }
    }

    fun getXLine(layer: Int, row: Row): Int? {
        if (model?.timeAxisModel != null) {
            val v1: Number? = model?.timeAxisModel?.getValue(layer, row)
            return if (v1 != null) {
                screenTransform.x.worldToScreen(v1.toDouble())
            } else {
                null
            }
        } else {
            return null
        }
    }

    fun getYLine(layer: Int, row: Row): Int? {
        if (model?.yAxisModel != null) {
            val v2: Number? = model?.yAxisModel?.getValue(layer, row)
            return if (v2 != null) {
                screenTransform.y.worldToScreen(v2.toDouble())
            } else {
                null
            }
        } else {
            return null
        }
    }

    private fun getMarkerSize(layer: Int, row: Row): Int {
        val sizeAxisModel: AxisModel<Row, Column>? = model!!.sizeAxisModel
        if (sizeAxisModel != null) {
            val v1 = sizeAxisModel.getValue(layer, row)

            if (v1 != null) {
                // Values between 1 and markerSize
                val range = 2 * sqrt((sizeAxisModel.maximum - sizeAxisModel.minimum) / PI)
                val v = 2 * sqrt((v1.toDouble() - sizeAxisModel.minimum) / PI)
                return ((v * ((markerSize.value!!.toInt()) - 1.0) / range)).toInt() + 1
            } else {
                return 0
            }
        } else {
            return markerSize.value!!.toInt()
        }
    }

    private inner class AnnotationIDrawing(private val selection: Selection<Any>, color: MkColor) :
        AbstractIDrawing() {
        private val color: MkColor = color

        private val selectionListener: SelectionListener<Any> = object : SelectionListener<Any> {
            override fun selectionChanged(event: SelectionEvent<Any>) {
                notifyIDrawingChanged()
            }
        }

        init {
            selection.addWeakSelectionListener(selectionListener)
        }

        override val isActive: Boolean
            get() = true

        override fun draw(g: IGraphics, point: Point2D?, width: Double, height: Double, clipBounds: Rectangle) {
            g.setColor(color)
            for (row in selection) {
                val mp: Point? = getPoint(AxisModel.ANNOTATION, row as Row)
                if (mp != null) {
                    g.drawLine(mp.x, 0.0, mp.x, height)
                    g.drawLine(0.0, mp.y, width, mp.y)

                    if (model?.sizeAxisModel != null) {
                        val size = getMarkerSize(AxisModel.ANNOTATION, row)
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    } else {
                        val size = getMarkerSize(AxisModel.ANNOTATION, row) + 2
                        val radius = size / 2.0
                        g.fillCircle(Rectangle((mp.x - radius).toInt(), (mp.y - radius).toInt(), size, size))
                    }
                } else {
                    val y = getYLine(AxisModel.ANNOTATION, row)
                    if (y != null) {
                        g.drawLine(0, y, width.toInt(), y)
                    } else {
                        val x = getXLine(AxisModel.ANNOTATION, row)
                        if (x != null) {
                            g.drawLine(x, 0, x, height.toInt())
                        }
                    }
                }
            }
        }
    }

        /**
         * Computes the euclidean distance between (x1,y1) and (x2,y2)
         *
         * @param x1
         * @param y1
         * @param x2
         * @param y2
         *
         * @return euclidean distance between (x1,y1) and (x2,y2)
         */
        fun distance(
            x1: Double, y1: Double,
            x2: Double, y2: Double
        ): Double {
            var x1 = x1
            var y1 = y1
            x1 -= x2
            y1 -= y2
            return sqrt(x1 * x1 + y1 * y1)
        }

        companion object {
        private const val MARGIN = 8

        private const val MULTITHREADED = true
        private val executor: ExecutorService? = CPHelper.instance.visualizationExecutorService()
    }

    private inner class RangePoint {
        var x: Int = 0
        var y: Int = 0
        var yMin: Int = 0
        var yMax: Int = 0

        fun setValue(x: Int, y: Int) {
            if (this.x == x) {
                if (y < yMin) {
                    yMin = y
                }
                if (y > yMax) {
                    yMax = y
                }
                this.y = y
            } else {
                this.x = x
                this.y = y
                this.yMin = y
                this.yMax = y
            }
        }
    }
}
